import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core'
import { MatButtonModule } from '@angular/material/button'
import { MatCardModule } from '@angular/material/card'
import { MatTooltipModule } from '@angular/material/tooltip'
import { ChargeGrouping, EstimateBalance, EstimateBalanceWorkflowStatus } from '@mmx/shared'
import { TranslateModule } from '@ngx-translate/core'

import { BalanceActionsToolbarComponent } from '../actions-toolbar/balance-actions-toolbar.component'
import {
  BalanceSendChannel,
  BalanceSummaryCapabilities,
  BalanceSummaryData,
  EMPTY_BALANCE_SUMMARY_DATA,
  NO_BALANCE_SUMMARY_CAPABILITIES,
} from '../balance-summary.types'
import {
  EMPTY_MULTI_VISIT_REVIEW,
  ModifyDiffViewModel,
  MultiVisitReviewRow,
  MultiVisitReviewViewModel,
} from '../services/estimate-balance-presenter.service'
import { BalanceGrandTotalBarComponent } from './components/balance-grand-total-bar/balance-grand-total-bar.component'
import { type BalanceChargeBreakdownDialogData } from './components/balance-charge-breakdown-dialog/balance-charge-breakdown-dialog.component'
import { BalanceStatusChipComponent } from './components/balance-status-chip/balance-status-chip.component'
import { BalanceVisitRowComponent } from './components/balance-visit-row/balance-visit-row.component'

@Component({
  selector: 'mmx-estimate-review-multi-visit',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslateModule,
    MatButtonModule,
    MatCardModule,
    MatTooltipModule,
    BalanceActionsToolbarComponent,
    BalanceStatusChipComponent,
    BalanceVisitRowComponent,
    BalanceGrandTotalBarComponent,
  ],
  templateUrl: './estimate-review-multi-visit.component.html',
  styleUrls: ['./estimate-review-multi-visit.component.scss'],
})
export class EstimateReviewMultiVisitComponent {
  @Input() vm: MultiVisitReviewViewModel = EMPTY_MULTI_VISIT_REVIEW
  @Input() busy = false
  @Input() hideFinalize = false
  @Input() diff: ModifyDiffViewModel | null = null

  // When hosted inside the modify-session accordion, the section's own
  // expansion-panel header names the section and the top summary card already
  // shows the status chip + actions. Drop this card's title block so the
  // status badge isn't rendered twice and the heading isn't duplicated.
  @Input() embedded = false

  // Non-finalize actions surface: previously rendered as a sibling toolbar
  // below the card, which left the History/Void buttons dangling in
  // finalized states. Now hosted inside the Review title bar as a kebab.
  @Input() summaryData: BalanceSummaryData = EMPTY_BALANCE_SUMMARY_DATA
  @Input() summaryCapabilities: BalanceSummaryCapabilities = NO_BALANCE_SUMMARY_CAPABILITIES
  @Input() needsPricing = false
  @Input() hideActions = false

  @Output() readonly finalize = new EventEmitter<void>()
  @Output() readonly save = new EventEmitter<void>()
  @Output() readonly revert = new EventEmitter<void>()
  @Output() readonly delete = new EventEmitter<void>()
  @Output() readonly send = new EventEmitter<BalanceSendChannel>()
  @Output() readonly sendDocument = new EventEmitter<void>()
  @Output() readonly viewHistory = new EventEmitter<void>()
  @Output() readonly printLetter = new EventEmitter<EstimateBalance>()
  @Output() readonly pbTrace = new EventEmitter<ChargeGrouping>()
  @Output() readonly hbTrace = new EventEmitter<ChargeGrouping>()
  @Output() readonly chargeBreakdown = new EventEmitter<BalanceChargeBreakdownDialogData>()

  get tooltipKey(): string {
    return this.isStatusBlocked ? 'balance.review.finalize-all-tooltip-disabled' : ''
  }

  get finalizeDisabled(): boolean {
    return this.busy || !this.vm.canFinalize
  }

  get isStatusBlocked(): boolean {
    const s = this.vm.workflowStatus
    return s === EstimateBalanceWorkflowStatus.CONFIRMED
      || s === EstimateBalanceWorkflowStatus.NA
      || s === EstimateBalanceWorkflowStatus.NOT_READY
  }

  onFinalize(): void {
    if (this.finalizeDisabled) {
      return
    }
    this.finalize.emit()
  }

  trackByRow(_index: number, row: MultiVisitReviewRow): number {
    return row.visitNumber
  }

  trackByRemoved(_index: number, removed: { visitNumber: number }): number {
    return removed.visitNumber
  }
}
